/* Big Gun V2 reviews (FE-06, 2026-10-05). One file, three jobs:
   1. The review data adapter (window.BGE.Reviews). The ONE place that reads review data, so the reviews page,
      the PDP store rating and the PDP zero-review panel always agree.
   2. /pages/reviews (templates/page.reviews.liquid): summary, star breakdown, search, sort, filters, pages.
   3. PDP with no reviews: the labelled store rating line, the store panel, and "Be the first to review",
      which opens Klaviyo's own review form for this product.

   Data path (phase 1, approved by bg-qa 2026-10-05): Klaviyo Reviews is the only home of review text. This
   reads the same onsite endpoint Klaviyo's own widget reads (published reviews only, company Rh3vMY). If it
   fails or its shape changes, the reviews page mounts Klaviyo's supported "all reviews" widget instead, and the
   PDP store rating stays hidden. Nothing is hard coded: every number comes from the live summary.
   Phase 2 (Ricky's decision): mirror published reviews into Shopify metaobjects so the text renders server side.

   Rules (FTC 16 CFR 465, Google review snippets): the store rating is always labelled "store rating" and never
   shown as a product's rating; it is never written to JSON-LD. Every published review shows, never filtered by
   stars unless the shopper picks a star filter. Reviews of other parts are labelled with the part they review. */
(function () {
  'use strict';

  var COMPANY = 'Rh3vMY';
  var API = 'https://fast.a.klaviyo.com/reviews/api/client_reviews/all/';
  var LIST_KEY = 'bgv2_rv_all_v2';
  var LIST_TTL = 30 * 60 * 1000;
  var FACTS_KEY = 'bgv2_rv_facts_v2';
  var FACTS_TTL = 24 * 60 * 60 * 1000;
  var PER_PAGE = 20;
  // Review photos: Klaviyo stores them on S3 and resizes them through its media service (same URLs its widget uses).
  var IMG_FULL = 'https://klaviyo.s3.amazonaws.com/reviews/images/';
  var IMG_THUMB = 'https://reviews-media.services.klaviyo.com/abc/width:192/height:192/resizing_type:fill/plain/';

  function qs(s, r) { return (r || document).querySelector(s); }
  function qsa(s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function readCache(key, ttl) {
    try { var o = JSON.parse(localStorage.getItem(key)); if (o && Date.now() - o.t < ttl) return o.v; } catch (e) {}
    return null;
  }
  function writeCache(key, v) { try { localStorage.setItem(key, JSON.stringify({ t: Date.now(), v: v })); } catch (e) {} }

  // ---------------------------------------------------------------------------
  // 1. Adapter
  function titleCase(s) { return String(s || '').replace(/(^|\s)(\S)/g, function (m, a, b) { return a + b.toUpperCase(); }); }

  function norm(r) {
    var p = r.product && !r.is_store ? r.product : null;
    return {
      id: String(r.id),
      rating: Math.round(Number(r.rating)) || 0,
      title: r.title || '',
      body: r.content || '',
      author: titleCase(r.author),
      verified: !!r.verified,
      store: !p,
      date: r.created_at || '',
      reply: r.public_reply_content ? { body: r.public_reply_content, author: r.public_reply_author || '' } : null,
      images: String(r.image_uuid || '').split(',').map(function (s) { return s.trim(); }).filter(function (s) { return /^[\w-]+\/[\w.-]+\.(jpe?g|png|webp|gif)(\?[\w=&.-]*)?$/i.test(s); }),
      part: r.product_variant && /\d/.test(r.product_variant.title || '') ? r.product_variant.title : '',
      product: p ? { handle: p.shopify_handle || p.handle || '', name: p.name || '', image: p.image_url || '' } : null
    };
  }

  function valid(d) {
    return d && Array.isArray(d.reviews) && d.summary && typeof d.summary.review_count === 'number' &&
      Array.isArray(d.summary.rating_histogram) && d.summary.rating_histogram.length === 5;
  }

  function fetchPage(offset) {
    var url = API + '?product_id=all&company_id=' + COMPANY + '&limit=100&offset=' + offset +
      '&sort=3&filter=&type=reviews&media=false&subtype=all';
    return fetch(url, { credentials: 'omit' }).then(function (r) {
      if (!r.ok) throw new Error('reviews ' + r.status);
      return r.json();
    }).then(function (d) {
      if (!valid(d)) throw new Error('reviews shape');
      return d;
    });
  }

  var pending = null;
  // Resolves to { summary: { avg, count, hist: [5..1] }, reviews: [...] }, or rejects.
  function getAll() {
    if (pending) return pending;
    var cached = readCache(LIST_KEY, LIST_TTL);
    if (cached) { pending = Promise.resolve(cached); return pending; }
    pending = (function loop(offset, acc, first) {
      return fetchPage(offset).then(function (d) {
        acc = acc.concat(d.reviews.filter(function (r) { return r && r.type !== 'question'; }).map(norm));
        first = first || d;
        if (d.has_more && offset < 1900 && d.reviews.length) return loop(offset + 100, acc, first);
        var s = first.summary, h = s.rating_histogram;
        var out = {
          summary: { avg: Number(s.star_rating) || 0, count: s.review_count, hist: [h[4], h[3], h[2], h[1], h[0]] },
          reviews: acc
        };
        writeCache(LIST_KEY, out);
        return out;
      });
    })(0, [], null);
    pending.catch(function () { pending = null; });
    return pending;
  }

  // Product facts (class, series, image, current URL) from the theme's own bgv2-facts, via the Section Rendering
  // API (sections/bgv2-facts-json.liquid). A missing product (404) is cached as gone: its name shows without a link.
  function getFacts(handles) {
    var cache = readCache(FACTS_KEY, FACTS_TTL) || {};
    var need = handles.filter(function (h) { return h && !cache[h]; });
    var i = 0;
    function worker() {
      if (i >= need.length) return Promise.resolve();
      var h = need[i++];
      return fetch('/products/' + encodeURIComponent(h) + '?sections=bgv2-facts-json', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) {
          var m = d && typeof d['bgv2-facts-json'] === 'string' && d['bgv2-facts-json'].match(/<script[^>]*data-bgv2-facts[^>]*>([\s\S]*?)<\/script>/);
          var f = null;
          try { f = m ? JSON.parse(m[1]) : null; } catch (e) {}
          cache[h] = f || { gone: true };
        }, function () {})
        .then(worker);
    }
    var workers = [];
    for (var w = 0; w < Math.min(4, need.length); w++) workers.push(worker());
    return Promise.all(workers).then(function () { writeCache(FACTS_KEY, cache); return cache; });
  }

  function fmtAvg(n) { return (Math.round(n * 10) / 10).toFixed(1); }
  function fmtDate(iso) {
    var d = new Date(iso);
    if (isNaN(d)) return '';
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/Los_Angeles' });
  }
  function isoDay(iso) { var d = new Date(iso); return isNaN(d) ? '' : d.toISOString().slice(0, 10); }
  function plural(n, one, many) { return n + ' ' + (n === 1 ? one : many); }
  function starsHtml(v, label) {
    return '<span class="bgv2-stars5" style="--r: ' + Math.round(v * 20) + '%" role="img" aria-label="' + esc(label) + '"></span>';
  }
  var VERIFIED_SVG = '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false"><path fill="currentColor" d="M12 1l3 2.3 3.7-.2.9 3.6 3.1 2.1-1.4 3.4 1.4 3.4-3.1 2.1-.9 3.6-3.7-.2L12 23l-3-2.3-3.7.2-.9-3.6-3.1-2.1 1.4-3.4-1.4-3.4 3.1-2.1.9-3.6 3.7.2z"/><path d="M8 12.5l2.6 2.6L16.5 9" fill="none" stroke="#141414" stroke-width="2"/></svg>';

  // Display series for a review: the theme's series, else the product family.
  var FAMILY = { tfi: 'TFI Power Box', rev: 'Rev Box', part: 'Parts and merch' };
  function seriesOf(f) { return f ? (f.series || FAMILY[f.family] || '') : ''; }
  function classOf(f) { return f ? (f.class || 'Universal') : ''; }

  // One review card. Shared by the reviews page and the PDP store panel.
  function card(r, f, opts) {
    opts = opts || {};
    var long = r.body.length > 320;
    var bodyId = 'bgv2-rv-b-' + r.id + (opts.idSuffix || '');
    var h = '<li class="bgv2-rv" id="review-' + esc(r.id) + (opts.idSuffix || '') + '">';
    h += '<div class="bgv2-rv__main">';
    h += starsHtml(r.rating, 'Rated ' + r.rating + ' out of 5');
    if (r.title) h += '<' + (opts.hTag || 'h3') + ' class="bgv2-rv__title">' + esc(r.title) + '</' + (opts.hTag || 'h3') + '>';
    h += '<p class="bgv2-rv__body' + (long ? ' is-clamped' : '') + '" id="' + bodyId + '">' + esc(r.body).replace(/\n/g, '<br>') + '</p>';
    if (long) h += '<button type="button" class="bgv2-textlink bgv2-rv__more" aria-expanded="false" aria-controls="' + bodyId + '">Read the full review</button>';
    h += '<p class="bgv2-rv__who"><b>' + esc(r.author || 'Big Gun customer') + '</b>';
    if (r.verified) h += '<span class="bgv2-rv__verified">' + VERIFIED_SVG + 'Verified buyer</span>';
    if (r.date) h += '<time datetime="' + isoDay(r.date) + '">' + fmtDate(r.date) + '</time>';
    h += '</p>';
    if (r.images.length) {
      h += '<ul class="bgv2-rv__photos" aria-label="Photos from ' + esc(r.author || 'the rider') + '">';
      r.images.forEach(function (u, i) {
        h += '<li><a class="bgv2-rv__photo" href="' + esc(IMG_FULL + u) + '" target="_blank" rel="noopener">' +
          '<img src="' + esc(IMG_THUMB + IMG_FULL + u) + '" width="96" height="96" loading="lazy" alt="Photo ' + (i + 1) + ' of ' + r.images.length +
          ' from ' + esc(r.author || 'the rider') + ', opens full size in a new tab"></a></li>';
      });
      h += '</ul>';
    }
    if (r.reply) {
      h += '<div class="bgv2-rv__reply"><p class="bgv2-rv__reply-k">Reply from ' + esc(r.reply.author || 'Big Gun Exhaust') + '</p><p>' +
        esc(r.reply.body).replace(/\n/g, '<br>') + '</p></div>';
    }
    h += '</div>';
    h += '<div class="bgv2-rv__prod">';
    if (r.product) {
      var gone = f && f.gone;
      var url = f && f.url ? f.url : '/products/' + encodeURIComponent(r.product.handle);
      var img = (f && f.image) || '';
      if (img) h += '<img class="bgv2-rv__thumb" src="' + esc(img) + '" width="56" height="56" alt="" loading="lazy">';
      h += '<div><span class="bgv2-rv__k">Review of</span>';
      h += gone ? '<span class="bgv2-rv__pname">' + esc(r.product.name) + '</span>'
                : '<a class="bgv2-rv__pname" href="' + esc(url) + '">' + esc((f && f.title) || r.product.name) + '</a>';
      if (r.part) h += '<span class="bgv2-rv__part">Part # ' + esc(r.part) + '</span>';
      var c = classOf(f), s = seriesOf(f);
      if (opts.tags !== false && (c || s)) {
        h += '<span class="bgv2-rv__tags">' + (c ? '<span class="bgv2-tag">' + esc(c) + '</span>' : '') +
          (s ? '<span class="bgv2-tag">' + esc(s) + '</span>' : '') + '</span>';
      }
      h += '</div>';
    } else {
      h += '<div><span class="bgv2-rv__k">Review of</span><span class="bgv2-rv__pname">Big Gun store and shipping</span></div>';
    }
    h += '</div></li>';
    return h;
  }

  function bindMore(root) {
    root.addEventListener('click', function (e) {
      var b = e.target.closest('.bgv2-rv__more');
      if (!b) return;
      var p = document.getElementById(b.getAttribute('aria-controls'));
      var open = b.getAttribute('aria-expanded') !== 'true';
      if (p) p.classList.toggle('is-clamped', !open);
      b.setAttribute('aria-expanded', open ? 'true' : 'false');
      b.textContent = open ? 'Show less' : 'Read the full review';
    });
  }

  window.BGE = window.BGE || {};
  window.BGE.Reviews = { getAll: getAll, getFacts: getFacts, card: card, fmtAvg: fmtAvg };

  // ---------------------------------------------------------------------------
  // 2. The reviews page
  function initPage(root) {
    var listEl = qs('[data-rvp-list]', root);
    var countEl = qs('[data-rvp-count]', root);
    var pagEl = qs('[data-rvp-pag]', root);
    var sumEl = qs('[data-rvp-summary]', root);
    var filtEl = qs('[data-rvp-filters]', root);
    var qEl = qs('[data-rvp-q]', root);
    var sortEl = qs('[data-rvp-sort]', root);
    var toggle = qs('[data-rvp-toggle]', root);
    var clearEl = qs('[data-rvp-clear]', root);
    var all = [], facts = {}, summary = null;

    var params = new URLSearchParams(location.search);
    var state = {
      q: params.get('q') || '',
      sort: params.get('sort') || 'newest',
      type: params.get('type') || 'all',
      cls: params.get('class') || '',
      series: params.get('series') || '',
      rating: parseInt(params.get('rating'), 10) || 0,
      photos: params.get('photos') === '1' ? '1' : '',
      page: Math.max(1, parseInt(params.get('page'), 10) || 1)
    };
    if (qEl) qEl.value = state.q;
    if (sortEl) sortEl.value = state.sort;

    function urlFor(s) {
      var p = new URLSearchParams();
      var keep = new URLSearchParams(location.search).get('view');
      if (keep) p.set('view', keep);
      if (s.q) p.set('q', s.q);
      if (s.sort !== 'newest') p.set('sort', s.sort);
      if (s.type !== 'all') p.set('type', s.type);
      if (s.cls) p.set('class', s.cls);
      if (s.series) p.set('series', s.series);
      if (s.rating) p.set('rating', s.rating);
      if (s.photos) p.set('photos', '1');
      if (s.page > 1) p.set('page', s.page);
      var str = p.toString();
      return location.pathname + (str ? '?' + str : '');
    }
    function slug(s) { return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''); }

    function matches(r, skip) {
      var f = r.product ? facts[r.product.handle] : null;
      if (skip !== 'type' && state.type === 'parts' && r.store) return false;
      if (skip !== 'type' && state.type === 'store' && !r.store) return false;
      if (skip !== 'cls' && state.cls && (r.store || slug(classOf(f)) !== state.cls)) return false;
      if (skip !== 'series' && state.series && (r.store || slug(seriesOf(f)) !== state.series)) return false;
      if (skip !== 'rating' && state.rating && r.rating !== state.rating) return false;
      if (state.photos && !r.images.length) return false;
      if (skip !== 'q' && state.q) {
        var hay = (r.title + ' ' + r.body + ' ' + r.author + ' ' + (r.product ? r.product.name : 'store shipping')).toLowerCase();
        var words = state.q.toLowerCase().split(/\s+/).filter(Boolean);
        for (var i = 0; i < words.length; i++) if (hay.indexOf(words[i]) === -1) return false;
      }
      return true;
    }

    function sorted(list) {
      var byDate = function (a, b) { return a.date < b.date ? 1 : a.date > b.date ? -1 : 0; };
      return list.slice().sort(function (a, b) {
        if (state.sort === 'highest') return b.rating - a.rating || byDate(a, b);
        if (state.sort === 'lowest') return a.rating - b.rating || byDate(a, b);
        return byDate(a, b);
      });
    }

    function renderSummary() {
      var s = summary, max = Math.max.apply(null, s.hist.concat(1));
      var h = '<h2 class="visually-hidden" id="bgv2-rvp-sum-h">Rating summary</h2>';
      h += '<div class="bgv2-rvp__score"><span class="bgv2-rvp__avg">' + fmtAvg(s.avg) + '</span><div>' +
        starsHtml(s.avg, fmtAvg(s.avg) + ' out of 5') +
        '<p class="bgv2-rvp__meta"><b>Big Gun store rating</b><br>' + plural(s.count, 'review', 'reviews') + ' across all parts</p></div></div>';
      h += '<ul class="bgv2-rvp__bars" aria-label="Filter by star rating">';
      for (var i = 0; i < 5; i++) {
        var star = 5 - i, n = s.hist[i] || 0;
        h += '<li><button type="button" class="bgv2-rvp__bar" data-rvp-rating="' + star + '" aria-pressed="' + (state.rating === star) + '"' +
          (n ? '' : ' disabled') + ' aria-label="' + star + ' star: ' + plural(n, 'review', 'reviews') + (n ? ', show only these' : '') + '">' +
          '<span aria-hidden="true">' + star + ' star</span><span class="bgv2-rvp__track" aria-hidden="true"><span style="width: ' +
          Math.round(n / max * 100) + '%"></span></span><span class="bgv2-rvp__n" aria-hidden="true">' + n + '</span></button></li>';
      }
      h += '</ul>';
      sumEl.innerHTML = h;
      sumEl.classList.remove('is-loading');
      sumEl.removeAttribute('aria-busy');
      sumEl.setAttribute('aria-labelledby', 'bgv2-rvp-sum-h');
    }

    function chipGroup(label, key, items) {
      if (!items.length) return '';
      var id = 'bgv2-rvp-g-' + key;
      var h = '<div class="bgv2-rvp__group" role="group" aria-labelledby="' + id + '"><p class="bgv2-rvp__glabel" id="' + id + '">' + label + '</p><div class="bgv2-rvp__chips">';
      items.forEach(function (it) {
        h += '<button type="button" class="bgv2-rvp__chip" data-rvp-key="' + key + '" data-rvp-val="' + esc(it.v) + '" aria-pressed="' +
          (String(state[key]) === String(it.v)) + '">' + esc(it.l) + ' <span class="bgv2-rvp__cn">' + it.n + '</span></button>';
      });
      return h + '</div></div>';
    }

    function tally(fn) {
      var m = {}, order = [];
      all.forEach(function (r) {
        if (r.store) return;
        var f = facts[r.product.handle];
        var l = fn(f);
        if (!l) return;
        if (!m[l]) { m[l] = 0; order.push(l); }
        m[l]++;
      });
      return order.sort(function (a, b) { return m[b] - m[a] || (a < b ? -1 : 1); }).map(function (l) { return { l: l, v: slug(l), n: m[l] }; });
    }

    function renderFilters() {
      var nStore = all.filter(function (r) { return r.store; }).length;
      var h = '<h2 class="visually-hidden">Filter reviews</h2>';
      h += chipGroup('Review of', 'type', [
        { l: 'All', v: 'all', n: all.length },
        { l: 'Parts', v: 'parts', n: all.length - nStore },
        { l: 'Store and shipping', v: 'store', n: nStore }
      ].filter(function (x) { return x.n > 0 || x.v === 'all'; }));
      var nPhotos = all.filter(function (r) { return r.images.length; }).length;
      if (nPhotos) h += chipGroup('Photos', 'photos', [{ l: 'With photos', v: '1', n: nPhotos }]);
      h += chipGroup('Machine', 'cls', tally(function (f) { return f && !f.gone ? classOf(f) : ''; }));
      h += chipGroup('Series', 'series', tally(function (f) { return f && !f.gone ? seriesOf(f) : ''; }));
      filtEl.innerHTML = h;
    }

    function syncPressed() {
      qsa('[data-rvp-key]', root).forEach(function (b) {
        b.setAttribute('aria-pressed', String(String(state[b.getAttribute('data-rvp-key')]) === b.getAttribute('data-rvp-val')));
      });
      qsa('[data-rvp-rating]', root).forEach(function (b) {
        b.setAttribute('aria-pressed', String(state.rating === +b.getAttribute('data-rvp-rating')));
      });
      var any = state.q || state.type !== 'all' || state.cls || state.series || state.rating || state.photos;
      if (clearEl) clearEl.hidden = !any;
      if (toggle) {
        var n = (state.type !== 'all') + !!state.cls + !!state.series + !!state.rating + !!state.photos;
        qs('[data-rvp-toggle-n]', toggle).textContent = n ? ' (' + n + ')' : '';
      }
    }

    function renderList(focusList) {
      var list = sorted(all.filter(function (r) { return matches(r); }));
      var pages = Math.max(1, Math.ceil(list.length / PER_PAGE));
      if (state.page > pages) state.page = pages;
      var start = (state.page - 1) * PER_PAGE;
      var slice = list.slice(start, start + PER_PAGE);
      listEl.innerHTML = slice.map(function (r) { return card(r, r.product ? facts[r.product.handle] : null); }).join('');
      listEl.removeAttribute('aria-busy');
      listEl.classList.remove('is-loading');
      if (!list.length) {
        countEl.textContent = 'No reviews match. Clear the filters to see all ' + plural(all.length, 'review', 'reviews') + '.';
      } else {
        countEl.textContent = 'Showing ' + (start + 1) + ' to ' + (start + slice.length) + ' of ' + plural(list.length, 'review', 'reviews');
      }
      var p = '';
      if (pages > 1) {
        var link = function (n, label, cur, rel) {
          var s = Object.assign({}, state, { page: n });
          return cur ? '<span class="bgv2-rvp__pg" aria-current="page">' + label + '</span>'
            : '<a class="bgv2-rvp__pg" href="' + esc(urlFor(s)) + '" data-rvp-page="' + n + '"' + (rel ? ' rel="' + rel + '"' : '') + '>' + label + '</a>';
        };
        if (state.page > 1) p += link(state.page - 1, 'Previous', false, 'prev');
        for (var i = 1; i <= pages; i++) p += link(i, String(i), i === state.page);
        if (state.page < pages) p += link(state.page + 1, 'Next', false, 'next');
      }
      pagEl.innerHTML = p;
      pagEl.hidden = !p;
      syncPressed();
      if (focusList) {
        var head = qs('[data-rvp-listhead]', root);
        if (head) { head.focus({ preventScroll: true }); head.scrollIntoView({ block: 'start' }); }
      }
    }

    function update(changes, push, focusList) {
      Object.assign(state, changes);
      history[push ? 'pushState' : 'replaceState'](null, '', urlFor(state));
      renderList(focusList);
    }

    root.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-rvp-key]');
      if (chip) {
        var k = chip.getAttribute('data-rvp-key'), v = chip.getAttribute('data-rvp-val');
        var ch = { page: 1 };
        ch[k] = (String(state[k]) === v && k !== 'type') ? '' : v;
        if (k === 'type' && ch.type === '') ch.type = 'all';
        if (k !== 'type' && ch[k] && state.type === 'store') ch.type = 'all';
        update(ch, false);
        return;
      }
      var bar = e.target.closest('[data-rvp-rating]');
      if (bar && !bar.disabled) {
        var star = +bar.getAttribute('data-rvp-rating');
        update({ rating: state.rating === star ? 0 : star, page: 1 }, false);
        return;
      }
      var pg = e.target.closest('[data-rvp-page]');
      if (pg) {
        e.preventDefault();
        update({ page: +pg.getAttribute('data-rvp-page') }, true, true);
        return;
      }
      if (e.target.closest('[data-rvp-clear]')) {
        if (qEl) qEl.value = '';
        // The button hides itself, so focus goes to the list heading and the live count says what happened.
        update({ q: '', type: 'all', cls: '', series: '', rating: 0, photos: '', page: 1 }, false, true);
        countEl.textContent = 'Filters cleared. ' + countEl.textContent;
        return;
      }
      if (toggle && e.target.closest('[data-rvp-toggle]')) {
        var open = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', String(open));
        filtEl.classList.toggle('is-open', open);
      }
    });
    var tq;
    if (qEl) qEl.addEventListener('input', function () {
      clearTimeout(tq);
      tq = setTimeout(function () { update({ q: qEl.value.trim(), page: 1 }, false); }, 250);
    });
    if (sortEl) sortEl.addEventListener('change', function () { update({ sort: sortEl.value, page: 1 }, false); });
    window.addEventListener('popstate', function () {
      var p = new URLSearchParams(location.search);
      state.page = Math.max(1, parseInt(p.get('page'), 10) || 1);
      renderList(true);
    });
    bindMore(root);

    function fail() {
      // Fallback: Klaviyo's own "all reviews" widget (supported embed; Klaviyo renders late-inserted widgets).
      qsa('[data-rvp-live]', root).forEach(function (el) { el.hidden = true; });
      var fb = qs('[data-rvp-fallback]', root);
      if (fb) {
        fb.hidden = false;
        if (!qs('#klaviyo-reviews-all', fb)) fb.insertAdjacentHTML('beforeend', '<div id="klaviyo-reviews-all" data-id="all"></div>');
      }
    }

    getAll().then(function (d) {
      if (!d.summary.count) { fail(); return; }
      all = d.reviews;
      summary = d.summary;
      renderSummary();
      var handles = [];
      all.forEach(function (r) { if (r.product && handles.indexOf(r.product.handle) === -1) handles.push(r.product.handle); });
      var timer = new Promise(function (res) { setTimeout(res, 5000); });
      return Promise.race([getFacts(handles).then(function (f) { facts = f; }), timer]).then(function () {
        renderFilters();
        renderList(false);
      });
    }).catch(fail);
  }

  // ---------------------------------------------------------------------------
  // 3. PDP with no reviews
  function initPdpZero(zone) {
    var line = qs('[data-bgv2-store-rating]');
    var panel = qs('[data-bgv2-store-panel]', zone);
    var allLink = qs('[data-bgv2-all-link]', zone);
    var first = qs('[data-bgv2-review-first]', zone);
    var mount = qs('[data-bgv2-kl-mount]', zone);
    var pid = zone.getAttribute('data-product-id');
    var series = zone.getAttribute('data-series') || '';

    // "Be the first to review": open Klaviyo's own review form for this product. The widget mounts on first
    // intent (Klaviyo picks up late-inserted widgets) and stays visually hidden; its Write a review button opens
    // Klaviyo's modal. If Klaviyo never renders, the link's own href (email) still works.
    function ensureMount() {
      if (!mount || !pid || qs('#klaviyo-reviews-all', mount)) return;
      mount.innerHTML = '<div id="klaviyo-reviews-all" data-id="' + esc(pid) + '"></div>';
    }
    function waitButton(ms) {
      return new Promise(function (res) {
        var t0 = Date.now();
        (function poll() {
          var b = mount && qs('.kl_reviews__button', mount);
          if (b) return res(b);
          if (Date.now() - t0 > ms) return res(null);
          setTimeout(poll, 150);
        })();
      });
    }
    if (first) {
      ['pointerenter', 'focus', 'touchstart'].forEach(function (ev) { first.addEventListener(ev, ensureMount, { once: true, passive: true }); });
      first.addEventListener('click', function (e) {
        if (!window.fetch || !pid) return;
        e.preventDefault();
        ensureMount();
        first.setAttribute('aria-busy', 'true');
        var label = first.textContent;
        first.textContent = 'Opening...';
        waitButton(6000).then(function (b) {
          first.removeAttribute('aria-busy');
          first.textContent = label;
          if (b) b.click();
          else window.location.href = first.getAttribute('href');
        });
      });
    }

    getAll().then(function (d) {
      var s = d.summary;
      if (!s.count) return;
      var avg = fmtAvg(s.avg);
      var label = 'Big Gun store rating ' + avg + ' out of 5 from ' + plural(s.count, 'review', 'reviews') + ' across all parts';
      if (line) {
        qs('[data-bgv2-store-avg]', line).textContent = avg;
        qs('[data-bgv2-store-n]', line).textContent = s.count;
        qs('[data-bgv2-store-w]', line).textContent = s.count === 1 ? 'review' : 'reviews';
        line.setAttribute('aria-label', label + '. This part has no reviews yet.');
        line.classList.add('is-ready');
      }
      if (allLink) allLink.textContent = s.count > 1 ? 'See all ' + s.count + ' Big Gun reviews' : 'See all Big Gun reviews';
      if (!panel) return;
      // Two most recent reviews of other parts in the same series, else the two most recent product reviews.
      // Picked by date only, never by stars.
      var byDate = d.reviews.filter(function (r) { return !r.store; }).sort(function (a, b) { return a.date < b.date ? 1 : -1; });
      var same = series ? byDate.filter(function (r) { return seriesFromName(r.product.name) === series; }) : [];
      var picks = (same.length ? same : byDate).slice(0, 2);
      var head = same.length ? 'Recent reviews of other ' + series + ' parts' : 'Recent Big Gun reviews';
      var h = '<p class="bgv2-store__line"><span class="bgv2-star1" aria-hidden="true"></span><strong>' + avg + '</strong> Big Gun store rating from ' +
        plural(s.count, 'review', 'reviews') + ' across all parts</p>';
      if (picks.length) {
        h += '<h3 class="bgv2-store__h">' + esc(head) + '</h3><ul class="bgv2-store__list">' +
          picks.map(function (r) { return card(r, null, { tags: false, hTag: 'h4', idSuffix: '-pdp' }); }).join('') + '</ul>';
      }
      panel.innerHTML = h;
      panel.hidden = false;
      bindMore(panel);
    }).catch(function () { /* store rating stays hidden; the empty state and both links still work */ });
  }

  // Series from a product name, longest match first (EXO Stainless before EXO). PDP picks only; the reviews
  // page uses the theme's own facts.
  var SERIES = ['EXO Stainless', 'EXO R', 'EXO', 'EVO U', 'EVO R', 'EVO M', 'EVO S', 'ECO', 'Explorer', 'Ballistic', 'Banshee', 'Warhead'];
  function seriesFromName(name) {
    var n = String(name || '').toUpperCase();
    for (var i = 0; i < SERIES.length; i++) if (n.indexOf(SERIES[i].toUpperCase() + ' ') === 0) return SERIES[i];
    return '';
  }

  function boot() {
    var page = qs('[data-bgv2-reviews-page]');
    if (page && window.fetch) initPage(page);
    var zero = qs('[data-bgv2-reviews-zero]');
    if (zero) initPdpZero(zero);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
